import { z } from "zod";

// ============================================================================
// SCHEMAS
// ============================================================================

export const TestTurnSchema = z.object({
  user_message: z.string(),
  expected_ai_response: z.string(),
});

export const CriterionEvaluationSchema = z.object({
  pass: z.boolean(),
  score: z.number(),
  explanation: z.string(),
});

export const TurnEvaluationSchema = z.object({
  overall_score: z.number(),
  criteria_scores: z.record(z.string(), CriterionEvaluationSchema).optional(),
  reasoning: z.string().optional(),
  strengths: z.array(z.string()).optional(),
  weaknesses: z.array(z.string()).optional(),
});

export const TurnResultSchema = z.object({
  turn_number: z.number(),
  user_message: z.string(),
  expected_ai_response: z.string(),
  actual_ai_response: z.string(),
  evaluation: TurnEvaluationSchema,
  intermediate_messages: z.string().optional(),
});

export const CustomAgentSchema = z.object({
  name: z.string(),
  description: z.string(),
  prompt: z.string(),
});

export const UserViewSchema = z
  .object({
    entity_type: z.string(),
    workflowId: z.string().optional(),
    runId: z.string().optional(),
  })
  .passthrough();

export const TestCaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  turns: z.array(TestTurnSchema),
  criteria: z.record(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
  updated_by: z.string().optional().nullable(),
  tags: z
    .array(z.string())
    .nullable()
    .optional()
    .transform(val => val ?? []),
  tenant: z.string().default("All"),
  team_id: z.string().optional().nullable(),
  custom_agents: z
    .array(CustomAgentSchema)
    .nullable()
    .optional()
    .transform(val => val ?? []),
  user_view: UserViewSchema.nullable().optional(),
});

export const EvalResultSchema = z.object({
  id: z.string(),
  test_case_name: z.string(),
  test_case_description: z.string().optional().nullable(),
  test_case_tags: z
    .array(z.string())
    .nullable()
    .optional()
    .transform(val => val ?? []),
  overall_score: z.number(),
  turn_results: z.array(TurnResultSchema),
  created_at: z.string(),
  session_id: z.string().optional().nullable(),
  run_name: z.string().optional().nullable(),
  tenant: z.string().default("All"),
  trace_id: z.string().optional().nullable(),
});

export const BatchRunResponseSchema = z.object({
  run_name: z.string().optional().nullable(),
  total: z.number(),
  successful: z.number(),
  failed: z.number(),
  results: z.array(EvalResultSchema),
});

export const PaginatedEvalResultsSchema = z.object({
  results: z.array(EvalResultSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
  has_next: z.boolean(),
  has_prev: z.boolean(),
});

export const CriterionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().optional().nullable(),
  updated_by: z.string().optional().nullable(),
  tenant: z.string().default("All"),
});

// ============================================================================
// INFERRED TYPES
// ============================================================================

export type TestTurn = z.infer<typeof TestTurnSchema>;
export type CriterionEvaluation = z.infer<typeof CriterionEvaluationSchema>;
export type TurnEvaluation = z.infer<typeof TurnEvaluationSchema>;
export type TurnResult = z.infer<typeof TurnResultSchema>;
export type CustomAgent = z.infer<typeof CustomAgentSchema>;
export type TestCase = z.infer<typeof TestCaseSchema>;
export type EvalResult = z.infer<typeof EvalResultSchema>;
export type PaginatedEvalResults = z.infer<typeof PaginatedEvalResultsSchema>;
export type BatchRunResponse = z.infer<typeof BatchRunResponseSchema>;
export type Criterion = z.infer<typeof CriterionSchema>;

// ============================================================================
// REQUEST/RESPONSE INTERFACES
// ============================================================================

export type UserView = z.infer<typeof UserViewSchema>;

export interface CreateTestCaseRequest {
  name: string;
  description?: string;
  turns: TestTurn[];
  criteria: Record<string, string>;
  tags?: string[];
  tenant?: string;
  team_id?: string | null;
  custom_agents?: CustomAgent[];
  user_view?: UserView | null;
}

export interface RunEvalRequest {
  test_case_id: string;
  session_id?: string;
}

export interface BatchRunEvalRequest {
  test_case_ids: string[];
  run_name?: string;
  session_id?: string;
}

export interface CreateCriterionRequest {
  name: string;
  description: string;
  tenant?: string;
}

export interface ActiveRun {
  id: string;
  name: string | null;
  total: number;
  completed: number;
  successful: number;
  failed: number;
  results: EvalResult[];
  testStatuses: Array<{
    testCaseId: string;
    testCaseName: string;
    status: "pending" | "running" | "passed" | "failed";
    score?: number;
    resultId?: string;
  }>;
}

// ============================================================================
// JOB STATUS SCHEMAS & TYPES
// ============================================================================

export const TestCaseStatusSchema = z.object({
  test_case_id: z.string(),
  test_case_name: z.string(),
  status: z.enum(["pending", "running", "passed", "failed"]),
  score: z.number().optional().nullable(),
  result_id: z.string().optional().nullable(),
});

export type TestCaseStatus = z.infer<typeof TestCaseStatusSchema>;

export const EvaluationJobStatusSchema = z.object({
  message: z.string().optional(),
  job_id: z.string(),
  status: z.enum(["submitted", "running", "completed", "failed"]),
  total: z.number(),
  completed: z.number(),
  successful: z.number(),
  failed: z.number(),
  test_statuses: z.array(TestCaseStatusSchema),
  agents_count: z.number(),
  criteria_count: z.number(),
});

export type EvaluationJobStatus = z.infer<typeof EvaluationJobStatusSchema>;

// ============================================================================
// BATCH EVALUATION SCHEMAS & TYPES
// ============================================================================

// Agent configuration for batch evaluation
export const AgentConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  prompt: z.string(),
  model: z.string(),
  is_local: z.boolean(),
});

export type AgentConfig = z.infer<typeof AgentConfigSchema>;

// Global context schema
export const GlobalContextSchema = z.object({
  datamate_ids: z.array(z.string()).default([]),
  knowledge_base_ids: z.array(z.number()).default([]),
});

export type GlobalContext = z.infer<typeof GlobalContextSchema>;

// Agent context override schema
export const AgentContextOverrideSchema = z.object({
  agent_id: z.string(),
  datamate_ids: z.array(z.string()).default([]),
  knowledge_base_ids: z.array(z.number()).default([]),
});

export type AgentContextOverride = z.infer<typeof AgentContextOverrideSchema>;

// Batch evaluation request schema
export const BatchEvaluationRequestSchema = z.object({
  agents: z.array(AgentConfigSchema),
  test_case_ids: z.array(z.string()),
  criteria: z.array(CriterionSchema),
  run_name: z.string().optional(),
  session_id: z.string().optional(),
  global_context: GlobalContextSchema.optional(),
  agent_context_overrides: z.array(AgentContextOverrideSchema).optional(),
});

export type BatchEvaluationRequest = z.infer<typeof BatchEvaluationRequestSchema>;

// Batch evaluation response schema
export const EvaluationJobResponseSchema = z.object({
  job_id: z.string(),
  message: z.string(),
  total_evaluations: z.number(),
  agents_count: z.number(),
  test_cases_count: z.number(),
  criteria_count: z.number(),
  status: z.string(),
});

export type EvaluationJobResponse = z.infer<typeof EvaluationJobResponseSchema>;

// Re-export agent and datamate types from split file
export * from "./agentTypes";
